import log from './Log';
import { AsyncStorageLike } from './types';

const DB_SERVICE_URI = 'luna://com.webos.service.db';

interface StorageRecord {
  _id: string;
  key: string;
  value: string;
}

let appIdPromise: Promise<string> | null = null;

const getAppId = (): Promise<string> => {
  if (!appIdPromise) {
    appIdPromise = Promise.resolve()
      .then(() => window.webOS.fetchAppId())
      .then(appId => {
        if (!appId) {
          throw new Error('webOS.fetchAppId() returned an empty app id');
        }
        return appId;
      })
      .catch(err => {
        appIdPromise = null;
        log.error(`db8Storage: failed to resolve app id: ${log.describeError(err)}`);
        throw err;
      });
  }
  return appIdPromise;
};

const getKindId = (appId: string) => `${appId}.storage:1`;

let kindReadyPromise: Promise<string> | null = null;

const ensureKind = (): Promise<string> => {
  if (!kindReadyPromise) {
    kindReadyPromise = getAppId()
      .then(
        appId =>
          new Promise<string>(resolve => {
            window.webOS.service.request(DB_SERVICE_URI, {
              method: 'putKind',
              parameters: {
                id: getKindId(appId),
                owner: appId,
                indexes: [{ name: 'key', props: [{ name: 'key' }] }],
                private: true,
              },
              onSuccess: () => resolve(getKindId(appId)),
              onFailure: (err: { errorCode?: number; errorText?: string }) => {
                kindReadyPromise = null;
                log.warn(`db8Storage: putKind failed: ${JSON.stringify(err)}`);
                resolve(getKindId(appId));
              },
            });
          }),
      )
      .catch(err => {
        // getAppId() rejected outright (e.g. empty app id) - allow a retry next call.
        kindReadyPromise = null;
        throw err;
      });
  }
  return kindReadyPromise;
};

const findRecord = (kindId: string, key: string): Promise<StorageRecord | null> =>
  new Promise(resolve => {
    window.webOS.service.request(DB_SERVICE_URI, {
      method: 'find',
      parameters: {
        query: {
          from: kindId,
          where: [{ prop: 'key', op: '=', val: key }],
        },
      },
      onSuccess: (response: { results: StorageRecord[] }) => {
        resolve(response.results?.[0] || null);
      },
      onFailure: (err: { errorCode?: number; errorText?: string }) => {
        log.error(`db8Storage: find failed for key "${key}": ${JSON.stringify(err)}`);
        resolve(null);
      },
    });
  });

const db8Storage: AsyncStorageLike = {
  getItem: async (key: string) => {
    try {
      const kindId = await ensureKind();
      const record = await findRecord(kindId, key);
      return record ? record.value : null;
    } catch (err) {
      log.error(`db8Storage: getItem("${key}") failed: ${log.describeError(err)}`);
      return null;
    }
  },

  setItem: async (key: string, value: string) => {
    try {
      const kindId = await ensureKind();
      const existing = await findRecord(kindId, key);
      return new Promise<void>(resolve => {
        if (existing) {
          window.webOS.service.request(DB_SERVICE_URI, {
            method: 'merge',
            parameters: { objects: [{ _id: existing._id, key, value }] },
            onSuccess: () => resolve(),
            onFailure: (err: { errorCode?: number; errorText?: string }) => {
              log.error(`db8Storage: merge failed for key "${key}": ${JSON.stringify(err)}`);
              resolve();
            },
          });
        } else {
          window.webOS.service.request(DB_SERVICE_URI, {
            method: 'put',
            parameters: { objects: [{ _kind: kindId, key, value }] },
            onSuccess: () => resolve(),
            onFailure: (err: { errorCode?: number; errorText?: string }) => {
              log.error(`db8Storage: put failed for key "${key}": ${JSON.stringify(err)}`);
              resolve();
            },
          });
        }
      });
    } catch (err) {
      log.error(`db8Storage: setItem("${key}") failed: ${log.describeError(err)}`);
      return undefined;
    }
  },

  removeItem: async (key: string) => {
    try {
      const kindId = await ensureKind();
      return new Promise<void>(resolve => {
        window.webOS.service.request(DB_SERVICE_URI, {
          method: 'del',
          parameters: {
            query: {
              from: kindId,
              where: [{ prop: 'key', op: '=', val: key }],
            },
          },
          onSuccess: () => resolve(),
          onFailure: (err: { errorCode?: number; errorText?: string }) => {
            log.error(`db8Storage: del failed for key "${key}": ${JSON.stringify(err)}`);
            resolve();
          },
        });
      });
    } catch (err) {
      log.error(`db8Storage: removeItem("${key}") failed: ${log.describeError(err)}`);
      return undefined;
    }
  },
};

export default db8Storage;
